/* eslint-disable @typescript-eslint/no-magic-numbers */
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { IoChevronBack } from 'react-icons/io5';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/AppRoutes';
import bulbIcon from '@/Assets/Images/bulb-icon.svg?url';
import disabledBulbIcon from '@/Assets/Images/disabled-bulb-icon.svg?url';
import yellowLockIcon from '@/Assets/Images/lock-icon-yellow.svg?url';
import Coins from '@/components/Coins';
import type { HeaderProperties } from '@/components/Header';
import LogoutStudentProfile from '@/components/LogoutStudentProfile';
import useSendEvents from '@/hooks/util/useSendEvents';
import useStore from '@/store';
import { getCoinBalance } from '@/utils/helper';

function HeaderDefault({
  headerTitle,
  children,
  showBack,
  showCoins,
  onBack: onBackOverride,
}: HeaderProperties): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const exitRoutes = new Set([ROUTES.INSTRUCTIONS]);
  const { activityEnabled, showSkillsCard, currentStudent } = useStore();
  const { skillEntryClickedEvent } = useSendEvents();
  const { t } = useTranslation(['translation']);

  const defaultOnBack = exitRoutes.has(location.pathname)
    ? () => navigate(ROUTES.HOME, { replace: true })
    : () => navigate(-1);
  const onBack = onBackOverride ?? defaultOnBack;

  const onSkillsClick = () => {
    skillEntryClickedEvent(currentStudent?.grade ?? '');
    navigate(ROUTES.COURSE_MODE);
  };

  return (
    <div className="min-w-screen flex h-screen flex-col overflow-hidden pt-[90px]">
      <header className="absolute top-0 z-elevated w-full bg-secondary-blue-3 p-4 text-neutral-white shadow-lg">
        <div className="container mx-auto flex items-center justify-center">
          <h1 className="font-lilita text-[2.5em] font-bold leading-[1.5]">{headerTitle}</h1>
          {showBack && (
            <button
              type="button"
              aria-label="Go Back"
              className="absolute left-7 z-modal flex h-[40px] w-[40px] items-center justify-center rounded-full border-[3px] border-primary-blue-3 bg-neutral-white"
              onClick={onBack}
            >
              <IoChevronBack style={{ fontSize: '25px', color: '#002F76' }} />
            </button>
          )}
          {!showBack && !showSkillsCard && (
            <button
              type="button"
              onClick={onSkillsClick}
              disabled={!activityEnabled}
              className="absolute left-10 z-modal flex h-[3.25rem] w-[6.9rem] cursor-pointer flex-row items-center justify-start rounded-r-[5.5em] bg-secondary-blue-7 pl-7"
              aria-label="Coins"
            >
              <img
                src={activityEnabled ? bulbIcon : disabledBulbIcon}
                alt=""
                className="absolute -left-6 h-[3.4rem]"
              />
              <p className="font-outline-2-black w-[2rem] text-center font-lilita text-[1.5rem] tracking-tighter text-neutral-white">
                {t('skills')}
              </p>
              {!activityEnabled && <img src={yellowLockIcon} alt="" className="ml-5" />}
            </button>
          )}
          <div className="absolute right-7 z-modal flex items-center gap-4">
            {showCoins && <Coins coins={getCoinBalance()} enableRedirection />}
            <LogoutStudentProfile showStudentProfile showLogout />
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

export default HeaderDefault;
